/**
 * Shared semantics for library multi-select filters that carry an "all" option.
 *
 * Every one of these controls behaves the same way: an empty selection means the
 * filter is off, "all" is mutually exclusive with the specific picks, and values
 * may arrive as either an array or a legacy comma-joined string. Keeping that in
 * one place is what lets the site filter, the sender filter, and anything added
 * later agree without each page re-deriving the rules.
 */

export const ALL_FILTER_VALUE = 'all';

/** Accepts both the array form and the legacy comma-joined string form. */
export function splitFilterValues(value: string | string[] | null | undefined): string[] {
  const source = Array.isArray(value) ? value : [value ?? ''];
  const values: string[] = [];
  for (const entry of source) {
    for (const part of String(entry ?? '').split(',')) {
      const trimmed = part.trim();
      if (trimmed) values.push(trimmed);
    }
  }
  return values;
}

/** Deduplicates and collapses an empty or "all"-bearing selection to `['all']`. */
export function normalizeFilterValues(
  value: string | string[] | null | undefined,
): string[] {
  const next: string[] = [];
  for (const item of splitFilterValues(value)) {
    if (!next.includes(item)) next.push(item);
  }
  if (next.length === 0 || next.includes(ALL_FILTER_VALUE)) return [ALL_FILTER_VALUE];
  return next;
}

/**
 * Applies a user's selection change. Newly ticking "all" wipes the specific
 * picks; ticking a specific value while "all" was on drops "all" instead of
 * leaving a contradictory selection.
 */
export function mergeFilterSelection(
  nextValue: string[],
  previousValue: string[],
): string[] {
  const previous = normalizeFilterValues(previousValue);
  const next: string[] = [];
  for (const item of splitFilterValues(nextValue)) {
    if (!next.includes(item)) next.push(item);
  }
  if (next.length === 0) return [ALL_FILTER_VALUE];
  const nextHasAll = next.includes(ALL_FILTER_VALUE);
  if (nextHasAll && !previous.includes(ALL_FILTER_VALUE)) return [ALL_FILTER_VALUE];
  const withoutAll = next.filter((item) => item !== ALL_FILTER_VALUE);
  return withoutAll.length > 0 ? withoutAll : [ALL_FILTER_VALUE];
}

export function filterStorageValue(values: string[]): string {
  const normalized = normalizeFilterValues(values);
  return normalized.includes(ALL_FILTER_VALUE) ? ALL_FILTER_VALUE : normalized.join(',');
}

/**
 * Drops values that no longer match an available option, so a remembered choice
 * cannot pin a library to something the viewer has since lost access to. `null`
 * means "nothing usable survived — fall back to the default".
 */
export function sanitizeFilterValues(
  values: string[] | null,
  optionIds: Set<string>,
  canSelectAll: boolean,
): string[] | null {
  if (!values) return null;
  const normalized = normalizeFilterValues(values);
  if (normalized.includes(ALL_FILTER_VALUE)) {
    return canSelectAll ? [ALL_FILTER_VALUE] : null;
  }
  const filtered = normalized.filter((value) => optionIds.has(value));
  return filtered.length > 0 ? filtered : null;
}

export function summarizeFilterSelection(
  values: string[],
  options: Array<{ id: string; label: string }>,
  allLabel: string,
): string {
  if (values.includes(ALL_FILTER_VALUE)) return allLabel;
  if (values.length === 1) {
    return options.find((option) => option.id === values[0])?.label ?? '1 selected';
  }
  return `${values.length} selected`;
}
